import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api, resolveMediaUrl } from '../../utils/api';

const PRIORITY_WINDOWS = [
  { label: '5:00 - 6:30 PM', start: '17:00', end: '18:30' },
  { label: '7:00 - 8:30 PM', start: '19:00', end: '20:30' },
  { label: '8:30 - 10:00 PM', start: '20:30', end: '22:00' },
];

function getNext7Days() {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export default function BookPriorityWindow() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [, setMemberships] = useState([]);
  const [branches, setBranches] = useState([]);
  const [artists, setArtists] = useState([]);
  const [services, setServices] = useState([]);

  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const days = useMemo(() => getNext7Days(), []);

  // Load memberships on mount
  useEffect(() => {
    (async () => {
      try {
        const mems = await api.getMyMemberships();
        setMemberships(Array.isArray(mems) ? mems : []);
        if (Array.isArray(mems) && mems.length > 0) {
          const shopId = mems[0].shop_id;
          const branchList = await api.getBranches(null, shopId);
          setBranches(Array.isArray(branchList) ? branchList : []);
        }
      } catch {
        setError(t('Failed to load memberships'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  // When branch changes, fetch artists and services
  useEffect(() => {
    if (!selectedBranchId) {
      setArtists([]);
      setServices([]);
      return;
    }

    (async () => {
      try {
        const [artistList, svcList] = await Promise.all([
          api.getArtists(null, Number(selectedBranchId)),
          api.getBranchServices(selectedBranchId),
        ]);
        const raw = Array.isArray(artistList) ? artistList : [];
        const vipEligible = raw.filter((a) =>
          ['vip', 'both'].includes(a.worker_tier || 'both')
        );
        setArtists(vipEligible);
        setServices(Array.isArray(svcList) ? svcList : []);
      } catch {
        setArtists([]);
        setServices([]);
      }
    })();

    setSelectedArtistId('');
    setSelectedServiceIds([]);
  }, [selectedBranchId]);

  const toggleService = (id) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const selectedServices = useMemo(
    () => services.filter((s) => selectedServiceIds.includes(s.id)),
    [services, selectedServiceIds]
  );

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (s.price || 0), 0),
    [selectedServices]
  );

  const selectedBranch = branches.find((b) => b.id === Number(selectedBranchId));
  const selectedArtist = artists.find((a) => a.id === Number(selectedArtistId));
  const selectedWindowData = selectedWindow !== null ? PRIORITY_WINDOWS[selectedWindow] : null;

  const canSubmit =
    selectedBranchId &&
    selectedArtistId &&
    selectedDate &&
    selectedWindow !== null &&
    selectedServiceIds.length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await api.createPremiumBooking({
        branch_id: Number(selectedBranchId),
        artist_id: Number(selectedArtistId),
        service_ids: selectedServiceIds,
        booking_date: selectedDate,
        window_start: selectedWindowData.start,
        window_end: selectedWindowData.end,
      });
      navigate('/premium/windows');
    } catch (err) {
      setError(err?.message || t('Failed to create booking'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={{ color: 'var(--orange)', textAlign: 'center', marginTop: 100 }}>
          {t('Loading...')}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>{t('Book Priority Window')}</h1>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.twoCol}>
        {/* LEFT COLUMN - Booking Form */}
        <div style={styles.leftCol}>
          {/* Branch Select */}
          <div style={styles.card}>
            <label style={styles.label}>{t('Select Branch')}</label>
            <select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              style={styles.select}
            >
              <option value="">{t('Choose a branch...')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Artist */}
          <div style={styles.card}>
            <label style={styles.label}>{t('Preferred Artist')}</label>
            <select
              value={selectedArtistId}
              onChange={(e) => setSelectedArtistId(e.target.value)}
              style={styles.select}
              disabled={!selectedBranchId}
            >
              <option value="">{t('premium.selectPreferredArtist')}</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {selectedArtist?.photo_url && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={resolveMediaUrl(selectedArtist.photo_url)}
                  alt={selectedArtist.name}
                  style={styles.artistAvatar}
                />
                <span style={{ color: 'var(--text-primary)' }}>{selectedArtist.name}</span>
              </div>
            )}
          </div>

          {/* Date Picker */}
          <div style={styles.card}>
            <label style={styles.label}>{t('Select Date')}</label>
            <div style={styles.dayRow}>
              {days.map((day) => {
                const val = formatDate(day);
                const active = val === selectedDate;
                return (
                  <button
                    key={val}
                    onClick={() => setSelectedDate(val)}
                    style={{
                      ...styles.dayBtn,
                      background: active ? 'var(--orange)' : 'var(--bg-card)',
                      color: active ? '#000' : 'var(--text-primary)',
                      border: active ? '2px solid var(--orange)' : '2px solid var(--border)',
                    }}
                  >
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 700 }}>{day.getDate()}</span>
                    <span style={{ fontSize: 10, opacity: 0.6 }}>
                      {day.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priority Window */}
          <div style={styles.card}>
            <label style={styles.label}>{t('Priority Window')}</label>
            <div style={styles.windowRow}>
              {PRIORITY_WINDOWS.map((win, idx) => {
                const active = selectedWindow === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedWindow(idx)}
                    style={{
                      ...styles.windowBtn,
                      background: active ? 'var(--orange)' : 'var(--bg-card)',
                      color: active ? '#000' : 'var(--text-primary)',
                      border: active ? '2px solid var(--orange)' : '2px solid var(--border)',
                    }}
                  >
                    {win.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div style={styles.card}>
            <label style={styles.label}>{t('Select Services')}</label>
            {!selectedBranchId && (
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                {t('Please select a branch first')}
              </p>
            )}
            {selectedBranchId && services.length === 0 && (
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                {t('No services available')}
              </p>
            )}
            <div style={styles.serviceList}>
              {services.map((svc) => {
                const checked = selectedServiceIds.includes(svc.id);
                return (
                  <label
                    key={svc.id}
                    style={{
                      ...styles.serviceItem,
                      border: checked ? '1px solid var(--orange)' : '1px solid var(--border)',
                      background: checked ? 'rgba(212, 175, 55, 0.06)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svc.id)}
                      style={{ accentColor: 'var(--orange)', width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <span style={{ flex: 1, color: 'var(--text-primary)' }}>{svc.name}</span>
                    <span style={{ color: 'var(--orange)', fontWeight: 600 }}>
                      {svc.price != null ? `${svc.price} EGP` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Booking Summary */}
        <div style={styles.rightCol}>
          <div style={styles.summaryCard}>
            <h2 style={styles.summaryTitle}>{t('Booking Summary')}</h2>

            {/* Selected Services */}
            <div style={styles.summarySection}>
              <h3 style={styles.summarySubtitle}>{t('Services')}</h3>
              {selectedServices.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>{t('No services selected')}</p>
              ) : (
                selectedServices.map((svc) => (
                  <div key={svc.id} style={styles.summaryRow}>
                    <span style={{ color: 'var(--text-primary)' }}>{svc.name}</span>
                    <span style={{ color: 'var(--orange)' }}>
                      {svc.price != null ? `${svc.price} EGP` : ''}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Total */}
            <div style={styles.totalRow}>
              <span style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>{t('Total')}</span>
              <span style={{ color: 'var(--orange)', fontSize: 22, fontWeight: 700 }}>
                {totalPrice} EGP
              </span>
            </div>

            {/* Details */}
            <div style={styles.summarySection}>
              <h3 style={styles.summarySubtitle}>{t('Details')}</h3>
              <div style={styles.detailRow}>
                <span style={{ color: 'var(--text-muted)' }}>{t('Date')}</span>
                <span style={{ color: 'var(--text-primary)' }}>
                  {selectedDate
                    ? formatDisplayDate(new Date(selectedDate + 'T00:00:00'))
                    : '-'}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={{ color: 'var(--text-muted)' }}>{t('Time Window')}</span>
                <span style={{ color: 'var(--text-primary)' }}>
                  {selectedWindowData ? selectedWindowData.label : '-'}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={{ color: 'var(--text-muted)' }}>{t('Branch')}</span>
                <span style={{ color: 'var(--text-primary)' }}>{selectedBranch?.name || '-'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={{ color: 'var(--text-muted)' }}>{t('Artist')}</span>
                <span style={{ color: 'var(--text-primary)' }}>{selectedArtist?.name || t('Any')}</span>
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                ...styles.submitBtn,
                opacity: canSubmit ? 1 : 0.4,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? t('Booking...') : t('CONFIRM PRIORITY WINDOW')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
    padding: '7rem 1.5rem 5rem',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    maxWidth: 1200,
    margin: '0 auto',
  },
  pageTitle: {
    color: 'var(--orange)',
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 30,
    letterSpacing: 1,
  },
  errorBanner: {
    background: '#3a1010',
    border: '1px solid #ff4444',
    color: '#ff6666',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 20,
    fontSize: 14,
  },
  twoCol: {
    display: 'flex',
    gap: 30,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  leftCol: {
    flex: '1 1 560px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  rightCol: {
    flex: '0 0 360px',
    position: 'sticky',
    top: 30,
  },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 20,
  },
  label: {
    display: 'block',
    color: 'var(--orange)',
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 12,
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    appearance: 'auto',
  },
  artistAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid var(--orange)',
  },
  dayRow: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  dayBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: 60,
    fontFamily: 'inherit',
  },
  windowRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  windowBtn: {
    flex: '1 1 140px',
    padding: '14px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    transition: 'all 0.2s',
    textAlign: 'center',
  },
  serviceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 280,
    overflowY: 'auto',
  },
  serviceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  summaryCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--orange)',
    borderRadius: 14,
    padding: 24,
  },
  summaryTitle: {
    color: 'var(--orange)',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 20,
    letterSpacing: 0.5,
  },
  summarySection: {
    marginBottom: 20,
  },
  summarySubtitle: {
    color: 'var(--orange)',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 0,
    marginBottom: 10,
    opacity: 0.8,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderTop: '1px solid var(--border)',
    borderBottom: '1px solid var(--border)',
    marginBottom: 20,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  },
  submitBtn: {
    width: '100%',
    padding: '16px',
    background: 'var(--orange)',
    color: '#000',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: 1.5,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: 10,
    transition: 'opacity 0.2s',
  },
};
